/**
 * Capability Coverage Widget (issue #1653, part of epic #1650)
 *
 * Shows which divisions support which capabilities, matching
 * `homepage-dashboard-spec.md`'s "Capability Coverage by Division" widget.
 * Administrators see every capability/division pair present; other users
 * see only capabilities with coverage in their own division (division is
 * the `parentTeamId` hierarchy-root, ADR-1254 — see
 * `backend/src/graphql/resolvers/capabilityCoverage.ts`). Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import { useCapabilityCoverage, type CapabilityCoverageRow } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './CapabilityCoverageWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'capability-coverage',
  title: 'Capability Coverage',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface CapabilityCoverageStats {
  matrix: CapabilityCoverageRow[];
}

type CapabilityCoverageQueryData = ReturnType<typeof useCapabilityCoverage>['data'];

function extractCapabilityCoverageStats(
  data: CapabilityCoverageQueryData
): CapabilityCoverageStats | null {
  if (!data?.capabilityCoverage) {
    return null;
  }
  return data.capabilityCoverage;
}

function rowTotal(row: CapabilityCoverageRow): number {
  return row.divisions.reduce((sum, d) => sum + d.count, 0);
}

export function CapabilityCoverageWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useCapabilityCoverage(),
    extractCapabilityCoverageStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.matrix.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="capability-coverage-widget__empty">
          <IconElement name="grid_view" className="capability-coverage-widget__empty-icon" />
          <p className="forge-typography--body2">No capability coverage data available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="capability-coverage-widget__list" data-testid="capability-coverage-list">
        {stats.matrix.map((row) => (
          <li
            key={row.capability}
            className="capability-coverage-widget__row"
            data-testid="capability-coverage-row"
          >
            <div className="capability-coverage-widget__row-header">
              <span className="capability-coverage-widget__capability-name">{row.capability}</span>
              <span className="capability-coverage-widget__total">{rowTotal(row)} total</span>
            </div>
            <div className="capability-coverage-widget__divisions">
              {row.divisions.map((entry) => (
                <span
                  key={entry.division}
                  className="capability-coverage-widget__division-chip"
                  data-testid="capability-coverage-division-chip"
                >
                  {entry.division}: {entry.count}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
